import { Component } from '@theme/component';
import { debounce, isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */

// Shared Back/Forward handling for storefront dialogs and drawers.
const panelHistoryDocument = String(Date.now()) + Math.random().toString(36).slice(2);
let panelHistorySequence = 0;
export class PanelHistory {
  #key = 'panel-' + ++panelHistorySequence;
  #applying = false;
  #pending = false;
  #restoreAgain = false;
  constructor(isOpen, open, close) {
    this.isOpen = isOpen;
    this.openPanel = open;
    this.closePanel = close;
  }
  connect() { window.addEventListener('popstate', this.#restore); }
  disconnect() { window.removeEventListener('popstate', this.#restore); }
  #keys() {
    const state = history.state?.herSwagPanels;
    return state?.document === panelHistoryDocument && Array.isArray(state.keys) ? state.keys : [];
  }
  opened() {
    if (this.#applying) return;
    const keys = this.#keys();
    if (keys.includes(this.#key)) return;
    try {
      history.pushState({ ...history.state, herSwagPanels: { document: panelHistoryDocument, keys: [...keys, this.#key] } }, '', location.href);
    } catch { /* Keep the panel usable if history is unavailable. */ }
  }
  closing() {
    if (this.#applying) return false;
    const keys = this.#keys();
    const index = keys.indexOf(this.#key);
    if (index < 0) return false;
    if (!this.#pending) {
      this.#pending = true;
      history.go(index - keys.length);
    }
    return true;
  }
  #restore = async () => {
    if (this.#applying) { this.#restoreAgain = true; return; }
    this.#pending = false;
    const shouldOpen = this.#keys().includes(this.#key);
    if (shouldOpen === this.isOpen()) return;
    this.#applying = true;
    try {
      if (shouldOpen) await this.openPanel();
      else await this.closePanel();
      await new Promise((resolve) => requestAnimationFrame(resolve));
    } finally {
      this.#applying = false;
      if (this.#restoreAgain) { this.#restoreAgain = false; this.#restore(); }
    }
  };
}

export class DialogComponent extends Component {
  requiredRefs = ['dialog'];
  #panelHistory = new PanelHistory(() => this.refs.dialog.open, () => this.showDialog(), () => this.closeDialog());

  connectedCallback() {
    super.connectedCallback();
    this.#panelHistory.connect();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#panelHistory.disconnect();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    unlockScroll(this.refs.dialog);
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    this.#panelHistory.opened();
    this.#previousScrollY = getScrollTop();

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      lockScroll(dialog);

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;
    if (this.#panelHistory.closing()) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    unlockScroll(dialog);
    scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          lockScroll(event.target);
        } else {
          unlockScroll(event.target);
        }
      }
    }
  },
  { capture: true }
);
